import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import { DataGrid, GRID_CHECKBOX_SELECTION_COL_DEF } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { backendFiltersTransformation } from '../../helpers';
import { useAvailableHeight, useIsMobile } from '../../hooks';
import {
  orderByToSortModel,
  sortModelToOrderBy,
} from '../tables/utils/selectionModelHandlers';
import DataGridCheckbox from './DataGridCheckbox';
import DataGridRowWithTooltip from './DataGridRowWithTooltip';
import DataGridLoader from './DataGridLoader';
import DataGridNoRowsOverlay from './DataGridNoRowsOverlay';
import DataGridRenderHeader from './DataGridRenderHeader';
import {
  calculateActualRowHeight,
  calculateHeaderHeight,
  calculateFooterHeight,
  calculateRowMargin,
  calculateScrollbarHeight,
  calculateTableBoxHeight,
} from '../../helpers/data-grid-box-height-calculator';
import { DEFAULT_DATA_GRID_CLASS_NAME, HIDE_SCROLLBAR_CLASS_NAME } from '../../theme/constants/data-grid';

const scrollerClass = makeStyles(() => ({
  virtualScroller: { overflowX: 'hidden', overflow: 'overlay' },
}));

const FingoDataGrid = ({
  rows,
  columns,
  includeHeaders,
  mobileHeaders,
  loadingWithSkeleton,
  rowsPerPageOptions,
  noRowsMessage,
  serverFilters,
  setColumnFilter,
  setOrderBy,
  orderBy,
  rowHeight,
  refetch,
  rowCount,
  page,
  pageSize,
  maxHeight,
  disableSelectionOnClick,
  outsideBoxProps,
  disabledCheckboxTooltip,
  disabledRowTooltip,
  DataGridNoRowsOverlayComponent = DataGridNoRowsOverlay,
  additionalQueriesResults,
  dataGridClassName,
  mobileDensity,
  getRowClassName,
  hideFooter,
  hideHorizontalScrollbar,
  getRowHeight,
  disableColumnSort,
  isRowSelectable,
  fitTableToContent,
  loadingSkeletonRowCount,
  ...restProps
}) => {
  const classes = scrollerClass();
  const gridRowCount = rowCount || rows.length;
  const gridPageSize = pageSize || Math.min(rows.length, 100);
  const [filterModel, _setFilterModel] = useState();
  const [height, refChange] = useAvailableHeight();
  const isMobile = useIsMobile();
  const boxRef = useRef(null);
  const setFilterModel = useCallback(
    (value) => {
      _setFilterModel(value);
      setColumnFilter(backendFiltersTransformation(value?.items?.[0]));
    },
    [setColumnFilter],
  );
  const checkboxWidth = useMemo(() => (isMobile ? 22 : 32), [isMobile]);
  const checkboxDef = useMemo(
    () => ({
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      minWidth: checkboxWidth,
      width: checkboxWidth,
      type: 'string',
      headerAlign: isMobile ? 'center' : 'right',
    }),
    [checkboxWidth, isMobile],
  );

  const fingoColumns = useMemo(() => {
    const dataGridColumns = columns.map((column) => ({
      ...column,
      ...(disableColumnSort ? { sortable: false } : {}),
      renderHeader: DataGridRenderHeader,
    }));
    return dataGridColumns.sort(
      (a, b) => includeHeaders.indexOf(a.field) - includeHeaders.indexOf(b.field),
    );
  }, [columns, includeHeaders, mobileHeaders, isMobile, disableColumnSort]);

  const visibleFields = useMemo(
    () => (isMobile ? mobileHeaders : includeHeaders),
    [isMobile, mobileHeaders, includeHeaders],
  );

  const setSortModel = useCallback(
    (model) => {
      const stringOrder = sortModelToOrderBy(model, fingoColumns);
      setOrderBy(stringOrder);
    },
    [setOrderBy, fingoColumns],
  );

  const sortModel = useMemo(
    () => orderByToSortModel(orderBy, fingoColumns, visibleFields),
    [orderBy, fingoColumns, visibleFields],
  );

  const serverProps = useMemo(
    () => ({
      paginationMode: 'server',
      sortingMode: 'server',
      filterMode: 'server',
      pagination: true,
      sortModel,
      filterModel,
      onFilterModelChange: setFilterModel,
      onSortModelChange: setSortModel,
    }),
    [setFilterModel, setSortModel, sortModel, filterModel],
  );

  // Helper for scrolling to the top after page change
  const handlePageChange = useCallback(() => {
    if (boxRef.current) {
      boxRef.current.scrollTo(0, 0);
    }
  }, [boxRef.current]);

  useEffect(() => {
    handlePageChange();
  }, [page, gridPageSize]);

  const { showHorizontalScrollbar, ...spreadableOutsideBoxProps } = outsideBoxProps;

  const dataGridStructure = useMemo(() => ({
    gridRowCount,
    gridPageSize,
    page,
    height,
    actualRowHeight: calculateActualRowHeight(rowHeight, isMobile, mobileDensity),
    rowMargin: calculateRowMargin(dataGridClassName),
    headerHeight: calculateHeaderHeight(isMobile, mobileDensity),
    footerHeight: calculateFooterHeight(hideFooter),
    scrollbarHeight: showHorizontalScrollbar ? calculateScrollbarHeight() : 0,
  }), [
    gridRowCount,
    gridPageSize,
    page,
    height,
    rowHeight,
    isMobile,
    dataGridClassName,
    hideFooter,
    showHorizontalScrollbar,
  ]);

  const fitHeightToRows = Boolean(
    fitTableToContent && (loadingSkeletonRowCount > 0 || gridRowCount > 0),
  );

  const boxHeight = calculateTableBoxHeight({
    fitTableToContent: fitHeightToRows,
    getRowHeight,
    loadingWithSkeleton,
    loadingSkeletonRowCount,
    maxHeight,
    ...dataGridStructure,
  });

  const columnVisibilityModel = useMemo(
    () => fingoColumns
      .map((column) => {
        if (includeHeaders.length) {
          return {
            [column.field]: isMobile
              ? mobileHeaders.includes(column.field)
              : includeHeaders.includes(column.field),
          };
        }
        return {
          [column.field]: true,
        };
      })
      .reduce((a, b) => ({ ...a, ...b })),
    [isMobile, fingoColumns],
  );
  // Enhance columns which need additional query results
  const enhancedColumns = useMemo(() => fingoColumns.map((column) => {
    if (!column.useAdditionalQueries) {
      return column;
    }

    return {
      ...column,
      renderCell: (params) => {
        if (column.renderCell) {
          const queryField = column.queryField || column.field;
          const columnResults = additionalQueriesResults?.[queryField];
          const key = `${params.row.id}-${queryField}-${columnResults?.loading}`;
          return (
            <div key={key}>
              {column.renderCell({
                ...params,
                additionalQueriesResults: columnResults ? {
                  [queryField]: {
                    loading: columnResults.loading ?? false,
                    error: columnResults.error,
                    data: columnResults.data,
                  },
                } : null,
              })}
            </div>
          );
        }
        return params.value;
      },
    };
  }), [fingoColumns, additionalQueriesResults]);
  return (
    <Box
      height={fitHeightToRows ? 'auto' : '100%'}
      overflow="auto"
      ref={boxRef}
      {...spreadableOutsideBoxProps}
    >
      <Box
        id="datagrid-box"
        ref={refChange}
        sx={{
          '& .littleHeader': {
            fontSize: 11,
          },
        }}
        height={boxHeight}
        width="100%"
        maxHeight={maxHeight}
      >
        <DataGrid
          className={[dataGridClassName, hideHorizontalScrollbar ? HIDE_SCROLLBAR_CLASS_NAME : ''].join(' ').trim()}
          getRowClassName={(params) => (getRowClassName(params.row))}
          columns={[checkboxDef, ...enhancedColumns]}
          rows={loadingWithSkeleton ? [] : rows}
          rowsPerPageOptions={rowsPerPageOptions}
          rowHeight={rowHeight || dataGridStructure.actualRowHeight}
          getRowHeight={getRowHeight}
          scrollbarSize={0}
          loading={loadingWithSkeleton}
          components={{
            LoadingOverlay: DataGridLoader,
            BaseCheckbox: DataGridCheckbox,
            NoRowsOverlay: DataGridNoRowsOverlayComponent,
            Row: DataGridRowWithTooltip,
          }}
          columnVisibilityModel={columnVisibilityModel}
          componentsProps={{
            baseCheckbox: { disabledCheckboxTooltip },
            noRowsOverlay: { Message: noRowsMessage },
            row: {
              disabledRowTooltip: (row) => disabledRowTooltip(row) || disabledCheckboxTooltip,
              isRowSelectable,
            },
          }}
          classes={{ virtualScroller: classes.virtualScroller }}
          rowCount={gridRowCount}
          page={page}
          pageSize={gridPageSize}
          disableSelectionOnClick={disableSelectionOnClick}
          density={isMobile ? mobileDensity : 'standard'}
          sx={{
            '& .MuiDataGrid-cell': {
              px: {
                xs: 0.25,
                md: 1,
              },
            },
            '& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox':
              {
                px: '0 !important',
              },
            '& .MuiDataGrid-columnHeader': {
              px: {
                xs: 0.25,
                md: 1.5,
              },
            },
          }}
          isRowSelectable={isRowSelectable}
          hideFooter={hideFooter}
          {...(serverFilters && serverProps)}
          {...restProps}
          onCellClick={(params, event) => {
            event.stopPropagation();
          }}
        />
      </Box>
    </Box>
  );
};

FingoDataGrid.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape).isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape),
  rowCount: PropTypes.number,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  parentBoxHeight: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  includeHeaders: PropTypes.arrayOf(PropTypes.string),
  mobileHeaders: PropTypes.arrayOf(PropTypes.string),
  loadingWithSkeleton: PropTypes.bool,
  noRowsMessage: PropTypes.func,
  serverFilters: PropTypes.bool,
  setColumnFilter: PropTypes.func,
  setOrderBy: PropTypes.func,
  rowHeight: PropTypes.number,
  orderBy: PropTypes.string,
  refetch: PropTypes.func,
  disabledCheckboxTooltip: PropTypes.string,
  disabledRowTooltip: PropTypes.func,
  isRowSelectable: PropTypes.func,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  maxHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  disableSelectionOnClick: PropTypes.bool,
  outsideBoxProps: PropTypes.shape({
    showHorizontalScrollbar: PropTypes.bool,
  }),
  DataGridNoRowsOverlayComponent: PropTypes.func,
  additionalQueriesResults: PropTypes.shape({}),
  dataGridClassName: PropTypes.string,
  mobileDensity: PropTypes.string,
  getRowClassName: PropTypes.func,
  hideFooter: PropTypes.bool,
  hideHorizontalScrollbar: PropTypes.bool,
  getRowHeight: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.oneOf(['auto']),
  ]),
  disableColumnSort: PropTypes.bool,
  fitTableToContent: PropTypes.bool,
  loadingSkeletonRowCount: PropTypes.number,
};

FingoDataGrid.defaultProps = {
  loadingWithSkeleton: false,
  noRowsMessage: () => (
    <Typography variant="body1" align="center">
      Sin filas
    </Typography>
  ),
  serverFilters: true,
  rowHeight: null,
  parentBoxHeight: null,
  mobileHeaders: [],
  includeHeaders: [],
  setColumnFilter: () => {},
  setOrderBy: () => {},
  orderBy: undefined,
  refetch: () => {},
  rowsPerPageOptions: [30, 50, 75, 100],
  maxHeight: null,
  rowCount: null,
  page: 0,
  pageSize: null,
  rows: [],
  disableSelectionOnClick: true,
  disabledCheckboxTooltip: '',
  disabledRowTooltip: () => '',
  isRowSelectable: null,
  outsideBoxProps: {
    showHorizontalScrollbar: false,
  },
  DataGridNoRowsOverlayComponent: DataGridNoRowsOverlay,
  additionalQueriesResults: {},
  dataGridClassName: DEFAULT_DATA_GRID_CLASS_NAME,
  mobileDensity: 'compact',
  getRowClassName: () => '',
  hideFooter: false,
  hideHorizontalScrollbar: false,
  getRowHeight: null,
  disableColumnSort: false,
  fitTableToContent: false,
  loadingSkeletonRowCount: null,
};

export default FingoDataGrid;
